/**
 * SchoolCertificates — admin module for certificate templates + issuing + approval.
 * 4 tabs: Dashboard, Issued, Templates, Audit.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';
import {
  Award, FileText, Loader2, Plus, CheckCircle2, XCircle, RotateCcw,
  Printer, QrCode, Shield, Copy
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}/api/school${path}`, {
    ...opts,
    headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json', ...opts.headers },
  });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Request failed'); }
  return res.json();
};

const CERT_TYPES = [
  { value: 'graduation', label: 'Graduation' },
  { value: 'academic', label: 'Academic Completion' },
  { value: 'merit', label: 'Merit / Excellence' },
  { value: 'training', label: 'Training Completion' },
  { value: 'ministry', label: 'Ministry / Commissioning' },
  { value: 'transcript', label: 'Transcript' },
  { value: 'recommendation', label: 'Recommendation Letter' },
  { value: 'internship', label: 'Internship' },
  { value: 'other', label: 'Other' },
];

const STATUS_META = {
  pending_approval: { label: 'Pending', color: 'bg-amber-100 text-amber-800' },
  approved: { label: 'Approved', color: 'bg-emerald-100 text-emerald-800' },
  rejected: { label: 'Rejected', color: 'bg-slate-200 text-slate-600' },
  revoked: { label: 'Revoked', color: 'bg-red-100 text-red-700' },
};

export const SchoolCertificates = ({ tenantId }) => {
  const qc = useQueryClient();
  const [tab, setTab] = useState('dashboard');
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showIssueForm, setShowIssueForm] = useState(false);

  const { data: stats } = useQuery({ queryKey: ['cert-dashboard', tenantId], queryFn: () => api(`/tenants/${tenantId}/certificates/stats/dashboard`) });
  const { data: templates = [] } = useQuery({ queryKey: ['cert-templates', tenantId], queryFn: () => api(`/tenants/${tenantId}/certificate-templates`) });
  const { data: certData } = useQuery({ queryKey: ['cert-list', tenantId], queryFn: () => api(`/tenants/${tenantId}/certificates?limit=100`) });
  const certs = certData?.items || [];
  const { data: audit = [] } = useQuery({ queryKey: ['cert-audit', tenantId], queryFn: () => api(`/tenants/${tenantId}/certificates/audit/list?limit=50`), enabled: tab === 'audit' });

  const approveMut = useMutation({
    mutationFn: id => api(`/tenants/${tenantId}/certificates/${id}/approve`, { method: 'POST' }),
    onSuccess: () => { toast.success('Certificate approved'); qc.invalidateQueries({ queryKey: ['cert-list'] }); qc.invalidateQueries({ queryKey: ['cert-dashboard'] }); },
    onError: e => toast.error(e.message),
  });
  const revokeMut = useMutation({
    mutationFn: ({ id, reason }) => api(`/tenants/${tenantId}/certificates/${id}/revoke`, { method: 'POST', body: JSON.stringify({ reason }) }),
    onSuccess: () => { toast.success('Revoked'); qc.invalidateQueries({ queryKey: ['cert-list'] }); qc.invalidateQueries({ queryKey: ['cert-dashboard'] }); },
    onError: e => toast.error(e.message),
  });

  const verifyUrl = (serial) => `${window.location.origin}/verify/${serial}`;

  return (
    <div className="space-y-4" data-testid="school-certificates">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2"><Award className="h-5 w-5 text-indigo-600" /> Certificates & Credentials</h2>
          <p className="text-xs text-slate-500">Issue, approve, and verify graduation / training / ministry credentials.</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => { setEditingTemplate(null); setShowTemplateForm(true); }} data-testid="new-template-btn">
            <Plus className="h-3.5 w-3.5 mr-1" /> Template
          </Button>
          <Button size="sm" onClick={() => setShowIssueForm(true)} disabled={!templates.length} data-testid="issue-cert-btn">
            <Award className="h-3.5 w-3.5 mr-1" /> Issue Certificate
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="issued" data-testid="tab-issued">Issued</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">Templates</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Audit</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="space-y-3 mt-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Issued Today', value: stats?.issued_today ?? 0, color: 'text-indigo-600', bg: 'bg-indigo-50' },
              { label: 'Pending Approval', value: stats?.pending_approval ?? 0, color: 'text-amber-600', bg: 'bg-amber-50' },
              { label: 'Approved', value: stats?.approved ?? 0, color: 'text-emerald-600', bg: 'bg-emerald-50' },
              { label: 'Revoked', value: stats?.revoked ?? 0, color: 'text-red-600', bg: 'bg-red-50' },
              { label: 'Total Issued', value: stats?.total ?? 0, color: 'text-slate-600', bg: 'bg-slate-50' },
              { label: 'Active Templates', value: stats?.templates ?? 0, color: 'text-blue-600', bg: 'bg-blue-50' },
              { label: 'Rejected', value: stats?.rejected ?? 0, color: 'text-slate-500', bg: 'bg-slate-50' },
            ].map((s, i) => (
              <Card key={i} className={s.bg}><CardContent className="p-4">
                <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                <p className="text-xs text-slate-600 mt-1">{s.label}</p>
              </CardContent></Card>
            ))}
          </div>

          <GraduatesQuickIssueCard tenantId={tenantId} templates={templates} onIssued={() => { qc.invalidateQueries({ queryKey: ['cert-list'] }); qc.invalidateQueries({ queryKey: ['cert-dashboard'] }); }} />
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Verification Portal</CardTitle>
              <CardDescription>Share this link with employers / institutions — they can verify any certificate issued here.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 bg-slate-50 border rounded-md px-3 py-2">
                <QrCode className="h-4 w-4 text-indigo-600" />
                <code className="flex-1 text-xs font-mono truncate">{window.location.origin}/verify</code>
                <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/verify`); toast.success('Copied'); }}>
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="issued" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Recipient</TableHead>
                  <TableHead>Serial</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Issued</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {certs.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400">No certificates issued yet.</TableCell></TableRow>}
                  {certs.map(c => (
                    <TableRow key={c.id} data-testid={`cert-row-${c.id}`}>
                      <TableCell>
                        <p className="font-medium text-sm">{c.recipient_name}</p>
                        <p className="text-[11px] text-slate-500">{c.template_name}</p>
                      </TableCell>
                      <TableCell><code className="text-xs font-mono">{c.serial}</code></TableCell>
                      <TableCell><Badge variant="outline" className="text-[10px] capitalize">{c.template_type}</Badge></TableCell>
                      <TableCell><Badge className={STATUS_META[c.status]?.color || ''}>{STATUS_META[c.status]?.label || c.status}</Badge></TableCell>
                      <TableCell className="text-xs text-slate-500">{c.issue_date}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {c.status === 'pending_approval' && (
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-600" onClick={() => approveMut.mutate(c.id)} disabled={approveMut.isPending} data-testid={`approve-${c.id}`}>
                              <CheckCircle2 className="h-4 w-4" />
                            </Button>
                          )}
                          {c.status === 'approved' && (
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => { const r = prompt('Reason for revocation:'); if (r) revokeMut.mutate({ id: c.id, reason: r }); }} data-testid={`revoke-${c.id}`}>
                              <XCircle className="h-4 w-4" />
                            </Button>
                          )}
                          <a href={`/certificates/${c.id}/print?tenant=${tenantId}`} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center h-7 w-7 rounded-md hover:bg-slate-100" title="Print">
                            <Printer className="h-4 w-4 text-slate-500" />
                          </a>
                          <a href={`${API_URL}/api/public/certificates/qr/${c.serial}.png?base_url=${encodeURIComponent(window.location.origin)}&size=10`} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center h-7 w-7 rounded-md hover:bg-slate-100" title="Open QR code">
                            <QrCode className="h-4 w-4 text-slate-500" />
                          </a>
                          <a href={verifyUrl(c.serial)} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center h-7 w-7 rounded-md hover:bg-slate-100" title="Verify publicly">
                            <Shield className="h-4 w-4 text-indigo-500" />
                          </a>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="templates" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {templates.map(t => (
              <Card key={t.id} data-testid={`template-${t.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">{t.name}</CardTitle>
                    <Badge variant="outline" className="text-[10px] capitalize">{t.type}</Badge>
                  </div>
                  <CardDescription className="text-xs">{t.title}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="text-xs text-slate-500 line-clamp-2">{(t.body_html || '').replace(/<[^>]*>/g, '').slice(0, 100)}</p>
                  <div className="flex gap-2 pt-2">
                    <Button size="sm" variant="outline" onClick={() => { setEditingTemplate(t); setShowTemplateForm(true); }}>Edit</Button>
                    <Badge variant="outline" className="text-[10px]">{t.requires_approval ? 'Approval required' : 'Auto-approved'}</Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
            {templates.length === 0 && (
              <Card className="col-span-full border-dashed">
                <CardContent className="py-10 text-center text-slate-400 text-sm">
                  No templates yet. Click <strong>+ Template</strong> to create your first graduation or training template.
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="audit" className="mt-4">
          <Card>
            <CardContent className="p-4">
              {audit.length === 0 ? (
                <p className="text-sm text-slate-400 py-6 text-center">No audit events yet.</p>
              ) : (
                <div className="space-y-1.5">
                  {audit.map(e => (
                    <div key={e.id} className="flex items-center justify-between text-xs border-b py-1.5 last:border-0">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="capitalize text-[10px]">{e.action}</Badge>
                        <span className="text-slate-500">{e.certificate_id || '—'}</span>
                        {e.meta?.serial && <code className="text-[10px] font-mono text-slate-500">{e.meta.serial}</code>}
                      </div>
                      <div className="text-slate-400">{e.by} · {e.at ? new Date(e.at).toLocaleString() : ''}</div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {showTemplateForm && (
        <TemplateFormDialog
          open={showTemplateForm}
          onClose={() => setShowTemplateForm(false)}
          template={editingTemplate}
          tenantId={tenantId}
          onSaved={() => { qc.invalidateQueries({ queryKey: ['cert-templates'] }); qc.invalidateQueries({ queryKey: ['cert-dashboard'] }); setShowTemplateForm(false); }}
        />
      )}

      {showIssueForm && (
        <IssueFormDialog
          open={showIssueForm}
          onClose={() => setShowIssueForm(false)}
          templates={templates}
          tenantId={tenantId}
          onIssued={() => { qc.invalidateQueries({ queryKey: ['cert-list'] }); qc.invalidateQueries({ queryKey: ['cert-dashboard'] }); setShowIssueForm(false); }}
        />
      )}
    </div>
  );
};

// -------- Template Form Dialog --------

function TemplateFormDialog({ open, onClose, template, tenantId, onSaved }) {
  const [form, setForm] = useState(() => template ? { ...template } : {
    name: '', type: 'graduation', title: '', body_html: '<p style="text-align:center;">This is to certify that <strong>{{recipient_name}}</strong> has successfully completed the <strong>{{program}}</strong> program on {{issue_date}}.</p>',
    orientation: 'landscape', requires_approval: true, is_active: true,
  });
  const saveMut = useMutation({
    mutationFn: d => api(
      template ? `/tenants/${tenantId}/certificate-templates/${template.id}` : `/tenants/${tenantId}/certificate-templates`,
      { method: template ? 'PUT' : 'POST', body: JSON.stringify(d) }
    ),
    onSuccess: () => { toast.success('Template saved'); onSaved?.(); },
    onError: e => toast.error(e.message),
  });
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>{template ? 'Edit' : 'New'} Certificate Template</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="template-name" /></div>
            <div><Label className="text-xs">Type</Label>
              <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}><SelectTrigger data-testid="template-type"><SelectValue /></SelectTrigger>
                <SelectContent>{CERT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label className="text-xs">Title (on the certificate)</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Certificate of Graduation" /></div>
          <div><Label className="text-xs">Body HTML</Label>
            <Textarea rows={6} value={form.body_html} onChange={e => setForm({ ...form, body_html: e.target.value })} className="font-mono text-xs" data-testid="template-body" />
            <p className="text-[11px] text-slate-400 mt-1">Placeholders: {'{{recipient_name}}, {{program}}, {{issue_date}}, {{serial}}, {{institution}}'}</p>
          </div>
          <div className="flex items-center justify-between py-2">
            <Label className="text-xs">Requires Approval</Label>
            <Switch checked={form.requires_approval} onCheckedChange={v => setForm({ ...form, requires_approval: v })} />
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
          <Button onClick={() => saveMut.mutate(form)} disabled={!form.name || saveMut.isPending} data-testid="save-template-btn">
            {saveMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> : null}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -------- Issue Certificate Dialog --------

function IssueFormDialog({ open, onClose, templates, tenantId, onIssued }) {
  const [mode, setMode] = useState('single');
  const [form, setForm] = useState({ template_id: templates[0]?.id || '', recipient_type: 'student', recipient_id: '' });
  const [batchFilters, setBatchFilters] = useState({ class_id: '', status: 'graduated' });

  const { data: studentsData } = useQuery({
    queryKey: ['students-for-issue', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/students?limit=200`),
  });
  const students = studentsData?.students || studentsData?.items || studentsData || [];

  const singleMut = useMutation({
    mutationFn: () => api(`/tenants/${tenantId}/certificates/issue`, { method: 'POST', body: JSON.stringify(form) }),
    onSuccess: r => { toast.success(`Issued: ${r.serial}`); onIssued?.(); },
    onError: e => toast.error(e.message),
  });
  const batchMut = useMutation({
    mutationFn: () => api(`/tenants/${tenantId}/certificates/issue-batch`, {
      method: 'POST', body: JSON.stringify({ template_id: form.template_id, recipient_type: form.recipient_type, filters: batchFilters })
    }),
    onSuccess: r => { toast.success(`Issued ${r.count} certificates`); if (r.errors?.length) toast.warning(`${r.errors.length} errors`); onIssued?.(); },
    onError: e => toast.error(e.message),
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Issue Certificate</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Tabs value={mode} onValueChange={setMode}>
            <TabsList><TabsTrigger value="single">Single</TabsTrigger><TabsTrigger value="batch">Batch</TabsTrigger></TabsList>
          </Tabs>
          <div><Label className="text-xs">Template</Label>
            <Select value={form.template_id} onValueChange={v => setForm({ ...form, template_id: v })}>
              <SelectTrigger data-testid="issue-template"><SelectValue placeholder="Choose…" /></SelectTrigger>
              <SelectContent>{templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {mode === 'single' ? (
            <div><Label className="text-xs">Student</Label>
              <Select value={form.recipient_id} onValueChange={v => setForm({ ...form, recipient_id: v })}>
                <SelectTrigger data-testid="issue-recipient"><SelectValue placeholder="Choose…" /></SelectTrigger>
                <SelectContent>{students.slice(0, 200).map(s => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name}{s.student_number ? ` (${s.student_number})` : ''}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          ) : (
            <div className="space-y-2">
              <Label className="text-xs">Batch filter — Status</Label>
              <Select value={batchFilters.status} onValueChange={v => setBatchFilters({ ...batchFilters, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['graduated', 'active', 'completed', 'enrolled'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-400">Will issue certificates for every student matching the filter. Each cert requires approval before becoming valid.</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
          {mode === 'single' ? (
            <Button onClick={() => singleMut.mutate()} disabled={!form.template_id || !form.recipient_id || singleMut.isPending} data-testid="issue-submit">
              {singleMut.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />}Issue
            </Button>
          ) : (
            <Button onClick={() => batchMut.mutate()} disabled={!form.template_id || batchMut.isPending} data-testid="issue-batch-submit">
              {batchMut.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />}Issue Batch
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default SchoolCertificates;

// -------- Graduates Quick-Issue Card --------

function GraduatesQuickIssueCard({ tenantId, templates, onIssued }) {
  const [templateId, setTemplateId] = useState('');
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const gradTemplates = (templates || []).filter(t => ['graduation', 'academic', 'ministry'].includes(t.type));

  const issueMut = useMutation({
    mutationFn: () => api(`/tenants/${tenantId}/certificates/issue-batch`, {
      method: 'POST',
      body: JSON.stringify({
        template_id: templateId,
        recipient_type: 'student',
        filters: { status: 'graduated', graduation_year: year },
      }),
    }),
    onSuccess: (r) => {
      if (r.count === 0) {
        toast.warning(`No graduates found for ${year}. Run 'Sync graduates → alumni' in Alumni Hub first.`);
      } else {
        toast.success(`Issued ${r.count} certificate${r.count === 1 ? '' : 's'}. ${r.errors?.length ? r.errors.length + ' errors' : ''}`);
      }
      onIssued?.();
    },
    onError: e => toast.error(e.message),
  });

  return (
    <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white" data-testid="grads-quick-issue">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2"><Award className="h-4 w-4 text-emerald-600" /> One-click: Issue certificates to graduates</CardTitle>
        <CardDescription>Stamps a batch of certificates for every student whose status is "graduated" matching the year. Each goes through the normal approval flow.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-0 sm:min-w-[200px]">
            <Label className="text-xs">Template</Label>
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger data-testid="grads-template-select"><SelectValue placeholder={gradTemplates.length ? 'Choose template…' : 'Create a graduation template first'} /></SelectTrigger>
              <SelectContent>{gradTemplates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="w-28">
            <Label className="text-xs">Graduation Year</Label>
            <Input value={year} onChange={e => setYear(e.target.value)} data-testid="grads-year" />
          </div>
          <Button
            onClick={() => issueMut.mutate()}
            disabled={!templateId || !year || issueMut.isPending}
            data-testid="grads-issue-btn"
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {issueMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> : <Award className="h-3.5 w-3.5 mr-2" />}
            Issue
          </Button>
        </div>
        <p className="text-[11px] text-slate-500 mt-2">Only students with <code className="bg-slate-100 px-1 rounded">status = graduated</code> and graduation year matching this field are selected.</p>
      </CardContent>
    </Card>
  );
}
